import React, { useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import ConnectionIndicator from '../../components/ConnectionIndicator';
import PreviewBanner from '../../components/PreviewBanner';
import { ErrorBoundary } from '../../components/ui';
import '../../components/AppLayout.css';
import './Child.css';

// The child pages, in the order they appear on the home page. Diary is last
// because it only appears once we know the gymnast has one, so it doesn't
// push the other tiles around when it arrives.
export function childPages(hasDiary, certificatesOn = true) {
  return [
    { to: '/child/level', label: 'My Level' },
    ...(certificatesOn ? [{ to: '/child/certificates', label: 'Certificates' }] : []),
    { to: '/child/routines', label: 'Routines' },
    ...(hasDiary ? [{ to: '/child/diary', label: 'Diary' }] : []),
  ];
}

// The whole app for a gymnast signed in with a code: the main app's header
// with a Home button instead of menus (signing out is on the home page).
// Anyone else is sent to their usual home.
export default function ChildLayout() {
  const { user, loading } = useAuth();
  const { branding, fetchBranding } = useBranding();
  const { pathname } = useLocation();
  const childId = user?.role === 'CHILD' ? user.id : null;

  // Branding loads when the app starts, before the child has signed in.
  useEffect(() => {
    if (childId) fetchBranding();
  }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/child-login" replace />;
  if (user.role !== 'CHILD') return <Navigate to="/dashboard" replace />;

  const atHome = pathname.replace(/\/+$/, '') === '/child';

  return (
    <div className="app-layout child-app">
      <header className="app-layout__nav child-app__nav">
        <div className="app-layout__topbar">
          <Link to="/child" className="app-layout__brand">
            {branding?.logoUrl && <img src={branding.logoUrl} alt="" className="app-layout__brand-logo" />}
            <span className="app-layout__brand-name">{branding?.clubName || user.club?.name || 'Trampoline Life'}</span>
          </Link>
          <div className="app-layout__user">
            {!atHome && <Link to="/child" className="app-layout__cart-link child-app__home">Home</Link>}
          </div>
        </div>
      </header>
      <div className="app-layout__content child-app__content">
        {/* Also turns on saving diary entries and routines with no signal */}
        <ConnectionIndicator />
        <PreviewBanner />
        <main className="app-layout__main child-app__main">
          <ErrorBoundary key={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
